<template>
	<div id="app">
		<!-- 初始化完成後才顯示路由內容 -->
		<router-view v-if="isAppReady" />

		<ByDialog
			class="clear-cart-confirm"
			:value="isCancelOrderDialog"
			width="320px"
			title="您的餐點已被取消"
			@closeDialog="onCloseCancelOrderDialog"
		>
			<template slot="dialog-footer">
				<div class="dialog-footer">
					<ByButton stl="main1" @click="onCloseCancelOrderDialog"
						>好的
					</ByButton>
				</div>
			</template>
		</ByDialog>

		<!--	網頁操作預期的彈窗	-->
		<ByDialog
			class="clear-cart-confirm"
			:value="operationHasFailed"
			width="560px"
			title="網頁操作逾期"
			@closeDialog="onCloseOperationHasFailedDialog"
		>
			<div>請刷新頁面後重新進行點餐</div>
			<span
				>網頁將在
				<span class="refresh-count-down"> {{ refreshCountDown }} </span>
				秒後自動重新整理...
			</span>

			<template slot="dialog-footer">
				<div class="dialog-footer">
					<ByButton stl="main1" @click="onCloseOperationHasFailedDialog"
						>立即重新整理
					</ByButton>
				</div>
			</template>
		</ByDialog>

		<!-- 全域 Loading 反饋 -->
		<ByLoadingFeedback
			:value="globalLoading.visible"
			:status="globalLoading.status"
			:message="globalLoading.message"
			:success-text="globalLoading.successText"
			:error-text="globalLoading.errorText"
			:show-result="globalLoading.showResult"
			:show-refresh-on-error="globalLoading.showRefreshOnError"
			:result-duration="globalLoading.resultDuration"
			:fullscreen="true"
			@close="onGlobalLoadingClose"
		/>
	</div>
</template>
<script>
import ByDialog from '@/components/Dialog.vue'
import ByButton from '@/components/Button.vue'
import ByLoadingFeedback from '@/components/ByLoadingFeedback.vue'
import { navigateTo, navigateToRefresh } from '@/utils/navigation'
import { resolveItemAutomaticDiscount } from '@/utils/order'
import {
	getMobilePreviewSessionId,
	setMobilePreviewRecoveryHandler,
} from '@/utils/mobilePreviewSession'

// guestLogin 重試相關常數
const GUEST_LOGIN_RETRY_LIMIT = 3
const GUEST_LOGIN_RETRY_KEY = 'guestLoginRetryCount'
const GUEST_LOGIN_RETRY_TIMESTAMP_KEY = 'guestLoginRetryTimestamp'
const GUEST_LOGIN_RETRY_RESET_TIME = 5 * 60 * 1000 // 5 分鐘（毫秒）

// 重連後補抓店家與預約設定的尾端合併窗口（與菜單頁同口徑）
const RECONNECT_SETTING_SYNC_INTERVAL = 10000

// 特殊路由：不走默認初始化流程，等待 API 回調設置 authToken 後再初始化
const SPECIAL_ROUTES = ['OrderConfirm', 'OrderCancel', 'TakeMeal']

/**
 * 檢查 sessionStorage 是否可用
 * @returns {boolean} true 表示可用
 */
function isStorageAvailable() {
	try {
		const testKey = '__storage_test__'
		sessionStorage.setItem(testKey, 'test')
		sessionStorage.removeItem(testKey)
		return true
	} catch (err) {
		console.warn('[App] sessionStorage 不可用:', err)
		return false
	}
}

export default {
	components: { ByButton, ByDialog, ByLoadingFeedback },
	data() {
		return {
			getContactLoad: true,
			getWebSocketLoad: true,
			pageTitle: '點溡線上點餐',
			refreshCountDown: 10,
			refreshInterval: null,
			initAutomaticDiscount: true,
			// 初始化載入相關
			isAppReady: false,
			hasInitialized: false, // 避免重複初始化
			isSpecialRouteInitializing: false, // 特殊路由是否正在等待 authToken
			hasCompletedSpecialRouteInit: false, // 特殊路由是否已完成後續初始化
			lastReconnectSettingSyncAt: 0,
			reconnectSettingSyncTimer: null,
			// 進行中的訪客登入（single-flight 鎖，見 handleGuestBuyer）
			guestLoginPromise: null,
		}
	},
	metaInfo() {
		return {
			title: this.pageTitle,
			meta: [
				{
					property: 'og:title',
					content: this.pageTitle,
					'data-react-helmet': true,
				},
			],
		}
	},
	computed: {
		isGuest() {
			return this.$store.state.user?.guestId
		},
		isCancelOrderDialog() {
			return this.$store.state.isCancelOrderDialog
		},
		qrCodeHash() {
			return this.$route.params.random || this.$storeManager.qrCodeHash
		},
		storeId() {
			return this.$storeManager.storeId
		},
		storeName() {
			return this.$storeManager.storeName
		},
		operationHasFailed() {
			return this.$store.state.user.operationHasFailed
		},
		isKiosk() {
			return this.$store.getters.isKiosk
		},
		isSpecialRoute() {
			return SPECIAL_ROUTES.includes(this.$route.name)
		},
		authToken() {
			return this.$store.state.user?.authToken || null
		},
		cart() {
			const cart = this.$store.getters['user/currCart'](this.qrCodeHash)
			return cart || []
		},
		cartTotal() {
			return this.$store.getters['user/cartTotal'](this.qrCodeHash) || 0
		},
		itemAutomaticDiscountList() {
			return this.$store.state.store.itemAutomaticDiscountList
		},
		orderAutomaticDiscountList() {
			return this.$store.state.store.orderAutomaticDiscountList
		},
		automaticDiscountListReady() {
			return this.$store.state.store.automaticDiscountListReady
		},
		refreshAutomaticDiscount() {
			return this.$store.state.store.refreshAutomaticDiscount
		},
		useOrderAutomaticDiscount() {
			const storeId = this.storeId
			if (!storeId) return null
			return this.$store.getters['user/useOrderAutomaticDiscountByStore'](
				storeId,
			)
		},
		cartHasFreeItem() {
			return this.$store.getters['user/currCartHasFreeItem'](this.qrCodeHash)
		},
		globalLoading() {
			return this.$store.state.globalLoading
		},
		reconnectSeq() {
			return this.$store.state.socket.reconnectSeq
		},
	},
	watch: {
		async $route(route, oldRoute) {
			const qrCodeHash = route.params.random

			if (
				this.hasInitialized &&
				oldRoute &&
				qrCodeHash !== oldRoute.params.random
			) {
				await this.ensurePlatformInfo()

				const kiosk = route.query.kiosk
				if (kiosk && kiosk === qrCodeHash) {
					await this.getStore()
					return
				}
				if (qrCodeHash) {
					await this.getStore()
					await this.checkBuyer()
				}
			}
			/**
			 * 自動登入邏輯：當檢測到以下所有條件時觸發
			 * - hasInitialized = true（已完成應用初始化，避免首次載入時觸發）
			 * - !isKiosk（非前台點餐模式，Kiosk 有自己的認證流程）
			 * - !authToken（沒有 token，需要重新登入）
			 * - !isSpecialRoute（非特殊路由，特殊路由有自己的初始化邏輯）
			 * - qrCodeHash 存在（有效的店家識別碼）
			 */
			if (
				this.hasInitialized &&
				!this.isKiosk &&
				!this.$store.state.kiosk.isKioskDomain &&
				!this.authToken &&
				!this.isSpecialRoute &&
				qrCodeHash
			) {
				$devLog('[App] 檢測到 authToken 為空，執行自動登入')
				try {
					await this.handleGuestBuyer()
				} catch (error) {
					console.error('[App] 自動登入失敗:', error)
				}
			}
		},
		async '$route.path'(e, v) {
			if (e && e !== v && v !== '/') {
				if (this.$route.meta.requiredStore && !this.$store.getters.isKiosk) {
					if (this.storeId && this.isGuest) {
						await this.getOpenInfo()
					}
				}
			}
		},
		async '$store.state.store.refreshSetting'(val, beforeVal) {
			if (val) {
				await this.getOpenInfo()
				this.$store.commit('store/SET_REFRESH_SETTING', false)
				this.checkWhatsEatBinding()
			}
		},
		reconnectSeq() {
			if (!this.hasInitialized || !this.storeId) return
			this.scheduleReconnectSettingRefresh()
		},
		operationHasFailed(val, beforeVal) {
			this.refreshInterval = window.setInterval(() => {
				if (this.refreshCountDown > 0) {
					this.refreshCountDown -= 1
				} else {
					window.clearInterval(this.refreshInterval)
					this.$store.commit('user/SET_OPERATION_HAS_FAILED', false)
					this.refresh()
				}
			}, 1000)
		},
		async authToken(newToken, oldToken) {
			if (
				!this.isSpecialRouteInitializing ||
				this.hasCompletedSpecialRouteInit ||
				!newToken
			) {
				return
			}

			$devLog('[App] 特殊路由檢測到 authToken 變化，執行後續初始化')
			await this.completeSpecialRouteInit()
		},
		cartTotal(val, beforeVale) {
			if (this.automaticDiscountListReady) {
				this.$store.commit('user/SET_CURRENT_ORDER_AUTOMATIC_DISCOUNT', {
					cartTotal: val,
					list: this.orderAutomaticDiscountList,
				})
			}
		},
		async refreshAutomaticDiscount(val, beforeVale) {
			if (val) {
				const isDiscountListReady = await this.getActiveAutomaticDiscountList()
				if (!isDiscountListReady) {
					this.$store.commit('store/SET_REFRESH_AUTOMATIC_DISCOUNT', false)
					return
				}

				this.$store.commit('user/CLEAR_CURRENT_ORDER_AUTOMATIC_DISCOUNT')

				if (this.cart.length > 0) {
					this.cartItemSetAutomaticDiscount()
					this.$store.commit('user/SET_CURRENT_ORDER_AUTOMATIC_DISCOUNT', {
						cartTotal: this.cartTotal,
						list: this.$store.state.store.orderAutomaticDiscountList,
					})
				}

				// 折扣異動不跳提示：顧客拿不到「改了哪些」的清單，訊息沒有可操作性，
				// 卻會在瀏覽菜單、選口味時打斷操作；購物車金額已在上方重算
				this.$store.commit('store/SET_REFRESH_AUTOMATIC_DISCOUNT', false)
			}
		},
		useOrderAutomaticDiscount(val, beforeVale) {
			if (!this.initAutomaticDiscount && val?.id !== beforeVale?.id) {
				if (beforeVale?.discountType === 'FREE_ITEM' && this.cartHasFreeItem) {
					const freeItemIndex = this.cart.findIndex(item => item.freeItem)
					if (freeItemIndex !== -1) {
						this.$store.commit('user/REMOVE_CART_ITEM', {
							index: freeItemIndex,
						})
					}
				}
			}
		},
	},
	created() {
		setMobilePreviewRecoveryHandler(this.recoverMobilePreviewSessionAsGuest)
		document.title = '點溡點餐平台'
		this.$store.commit('user/SET_STORE', {
			qrCodeHash: null,
			storeId: null,
			storeName: '',
		})
		// 清除上次殘留的取餐選擇（桌號、取餐時間等），每次載入視為新的點餐 session
		this.$store.commit('user/RESET_PICKUPMETHOD')
		this.$store.commit('user/CLEAR_PICKUP_SELECTIONS')

		this.$router.onReady(async () => {
			if (this.hasInitialized) {
				return
			}

			const qrCodeHash = this.$route.params.random

			try {
				if (qrCodeHash) {
					await this.initializeWithQrCode()
				} else {
					await this.initializeWithoutQrCode()
				}
			} finally {
				this.hasInitialized = true
				// 清除上次 session 殘留的購物車衝突紅框：conflicts 隨 user 模組持久化，
				// 但它是「某次比對當下」的結果，跨重開已過期。送單前必經 compareCart 會重新標記
				this.$store.commit('user/CLEAR_CART_CONFLICTS')
			}
		})
	},
	beforeDestroy() {
		setMobilePreviewRecoveryHandler(null)
		if (this.reconnectSettingSyncTimer) {
			clearTimeout(this.reconnectSettingSyncTimer)
			this.reconnectSettingSyncTimer = null
		}
	},
	methods: {
		async initializeWithQrCode() {
			const qrCodeHash = this.$storeManager.qrCodeHash
			let isKioskMode = this.$route.query.kiosk === qrCodeHash
			let loginType = isKioskMode ? 'FRONT' : 'GUEST'

			try {
				await this.ensurePlatformInfo()

				if (this.$store.state.kiosk.isKioskDomain) {
					isKioskMode = true
					loginType = 'FRONT'
				}
			} catch (error) {
				console.error('[App] 初始化平台參數失敗:', error)
				this.isAppReady = true
			}

			if (this.isSpecialRoute) {
				try {
					await this.getStoreInfo(qrCodeHash, loginType)

					if (this.$store.state.maintenance.modal) {
						throw new Error('系統維護中，請稍後再試')
					}

					this.isAppReady = true

					if (this.authToken || getMobilePreviewSessionId()) {
						this.isSpecialRouteInitializing = true
						$devLog(
							'[App] 特殊路由檢測到已存在的 authToken，直接執行後續初始化',
						)
						await this.completeSpecialRouteInit()
					} else {
						this.isSpecialRouteInitializing = true
					}
				} catch (error) {
					console.error('[App] 特殊路由初始化失敗:', error)
					this.isAppReady = true
				}
				return
			}

			const state = {
				currentBuyerData: null,
				isUserLoggedIn: false,
			}

			// 前台登入的 token 才是 Kiosk 身份的真相來源：網址無 ?kiosk= 又非 Kiosk 域名時
			// （例如本地開發重整），上面兩個依據都會把前台誤判成訪客。
			// 同 handleUserBuyer、completeSpecialRouteInit 的判定，三處要改一起改。
			// 權限檢查不能放這裡：judgeHasStorePermission 依賴 storeId，而首次造訪該店時
			// storeManager 還沒有值（要等 getStore 寫入），交由 createKioskStages 在其後執行。
			let hasResolvedBuyer = false
			try {
				const response = await this.$api.user.getCurrentBuyer()
				hasResolvedBuyer = true

				if (response.errorCode === 'SUCCESS') {
					state.currentBuyerData = response.data
					state.isUserLoggedIn = true

					if (response.data.loginType === 'FRONT') {
						isKioskMode = true
						loginType = 'FRONT'
					}
				}
			} catch (error) {
				if (error.status === 401) {
					// 未登入是訪客的正常狀態，不需要 createCommonStages 再打一次
					hasResolvedBuyer = true
					state.isUserLoggedIn = false
				} else {
					// 此處 throw 會逃到 created 的 try/finally（無 catch）導致 isAppReady 永遠設不上，
					// 故不處理，留給 createCommonStages 重試並沿用既有錯誤語意
					$devLog('[App] 提前取得買家身份失敗，交由初始化階段重試:', error)
				}
			}

			const stages = [
				...this.createCommonStages(qrCodeHash, loginType, () => state, {
					skipGetCurrentBuyer: hasResolvedBuyer,
				}),
				...(isKioskMode
					? this.createKioskStages(() => state)
					: this.createGuestStages(() => state)),
			]

			const success = await this.$loading.runStages(stages)

			if (!success) {
				this.isAppReady = true
			}
		},

		createCommonStages(qrCodeHash, loginType, getState, options = {}) {
			return [
				{
					text: '正在初始化環境...',
					executor: async () => {
						await this.getStoreInfo(qrCodeHash, loginType)

						if (this.$store.state.maintenance.modal) {
							throw new Error('系統維護中，請稍後再試')
						}

						if (options.skipGetCurrentBuyer) {
							return true
						}

						const state = getState()
						try {
							const response = await this.$api.user.getCurrentBuyer()
							if (response.errorCode === 'SUCCESS') {
								state.currentBuyerData = response.data
								state.isUserLoggedIn = true
							}
						} catch (error) {
							if (error.status === 401) {
								state.isUserLoggedIn = false
							} else {
								throw error
							}
						}

						return true
					},
					minDuration: 100,
				},
			]
		},

		createGuestStages(getState) {
			return [
				{
					text: '建立連線中...',
					executor: async () => {
						const { isUserLoggedIn, currentBuyerData } = getState()
						this.$store.commit('kiosk/SET_IS', false)

						if (!isUserLoggedIn) {
							const response = await this.$api.user.guestLogin()
							this.$store.commit('user/SET_GUEST_ID', response.data.account)
							this.$store.commit('user/SET_WS_TOKEN', response.wsToken)

							if (isStorageAvailable()) {
								sessionStorage.removeItem(GUEST_LOGIN_RETRY_KEY)
								sessionStorage.removeItem(GUEST_LOGIN_RETRY_TIMESTAMP_KEY)
							}

							const urlParams = new URLSearchParams(window.location.search)
							if (
								urlParams.has('_guestLoginRetry') ||
								urlParams.has('_guestLoginRetryTime')
							) {
								urlParams.delete('_guestLoginRetry')
								urlParams.delete('_guestLoginRetryTime')
								const newUrl = urlParams.toString()
									? `${window.location.pathname}?${urlParams.toString()}`
									: window.location.pathname
								window.history.replaceState({}, '', newUrl)
							}
						} else {
							const response = await this.$api.user.getWsToken()
							this.$store.commit('user/SET_WS_TOKEN', response.data)

							if (currentBuyerData.account) {
								this.$store.commit(
									'user/SET_GUEST_ID',
									currentBuyerData.account,
								)
							}
						}

						this.isAppReady = true

						this.initWebSocket()

						return true
					},
					minDuration: 10,
				},
				{
					text: '載入店家資訊...',
					executor: async () => {
						await this.getOpenInfo()

						this.checkWhatsEatBinding().catch(err => {
							console.warn('[App] checkWhatsEatBinding failed:', err)
						})

						return true
					},
					minDuration: 10,
				},
			]
		},

		createKioskStages(getState) {
			return [
				{
					text: '建立連線中...',
					executor: async () => {
						const { isUserLoggedIn, currentBuyerData } = getState()

						if (!isUserLoggedIn) {
							await this.$store.dispatch('user/clearKioskSession')

							this.isAppReady = true

							await navigateToRefresh(this.$route.params.random)

							$devLog('需要登入')
							this.$loading.hide()
							return false
						}

						const hasPermission = this.judgeHasStorePermission(currentBuyerData)
						if (!hasPermission || currentBuyerData.loginType !== 'FRONT') {
							await this.$store.dispatch('user/clearKioskSession')

							this.isAppReady = true

							await navigateToRefresh(this.$route.params.random)

							$devLog('權限不足或登入類型錯誤')
							this.$loading.hide()
							return false
						}

						this.$store.commit('kiosk/SET_IS', true)

						const response = await this.$api.user.getWsToken()
						this.$store.commit('user/SET_WS_TOKEN', response.data)

						// 這個順序是正確的（避免畫面閃爍），只是與 Guest 模式順序不同
						if (this.$route.name === 'Refresh') {
							await navigateTo(`/${this.$route.params.random}/Store`, {
								useReplace: true,
							})
						}
						this.isAppReady = true

						this.initWebSocket()

						return true
					},
					minDuration: 10,
				},
				{
					text: '載入店家資訊...',
					executor: async () => {
						await this.getOpenInfo()
						return true
					},
					minDuration: 10,
				},
			]
		},

		async completeSpecialRouteInit() {
			if (this.hasCompletedSpecialRouteInit) {
				$devLog('[App] 特殊路由已完成初始化，跳過重複執行')
				return
			}

			try {
				const response = await this.$api.user.getCurrentBuyer()
				if (response.errorCode !== 'SUCCESS') {
					throw new Error('getCurrentBuyer failed')
				}
				const currentBuyerData = response.data

				const isKioskMode = currentBuyerData.loginType === 'FRONT'
				this.$store.commit('kiosk/SET_IS', isKioskMode)

				if (!isKioskMode && currentBuyerData.buyerType === 'GUEST') {
					this.$store.commit('user/SET_GUEST_ID', currentBuyerData.account)
				}

				const wsTokenRes = await this.$api.user.getWsToken()
				this.$store.commit('user/SET_WS_TOKEN', wsTokenRes.data)
				this.initWebSocket()

				if (this.storeId) {
					await this.getOpenInfo()
					this.checkWhatsEatBinding().catch(err => {
						console.warn('[App] checkWhatsEatBinding failed:', err)
					})
				}

				this.hasCompletedSpecialRouteInit = true
				this.isSpecialRouteInitializing = false
				$devLog('[App] 特殊路由後續初始化完成')
			} catch (error) {
				console.error('[App] 特殊路由後續初始化失敗:', error)
				this.isSpecialRouteInitializing = false
			}
		},

		async recoverMobilePreviewSessionAsGuest() {
			await this.handleGuestBuyer()
			if (!this.authToken) {
				throw new Error('無法建立普通遊客連線')
			}
			this.initWebSocket()
		},

		async initializeWithoutQrCode() {
			await this.ensurePlatformInfo()
			this.isAppReady = true
		},

		async getStoreInfo(qrCodeHash, loginType) {
			const res = await this.$api.getStore({ qrCodeHash, loginType })
			const storeName = res.data.name

			this.$store.commit('user/SET_STORE', {
				qrCodeHash,
				storeId: res.data.id,
				storeName: storeName,
			})

			document.title = storeName ? storeName : '點溡點餐平台'
			this.pageTitle = storeName ? storeName + ' | 線上點餐' : '點溡點餐平台'

			this.$store.commit('user/CHECK_CART_AND_SET')

			const isDiscountListReady = await this.getActiveAutomaticDiscountList()

			if (isDiscountListReady) {
				this.$store.commit('user/CLEAR_CURRENT_ORDER_AUTOMATIC_DISCOUNT')
				if (this.cart.length > 0) {
					this.cartItemSetAutomaticDiscount()
				}
				this.$store.commit('user/SET_CURRENT_ORDER_AUTOMATIC_DISCOUNT', {
					cartTotal: this.cartTotal,
					list: this.orderAutomaticDiscountList,
				})
				if (this.cartHasFreeItem) {
					this.checkFreeItem()
				}
			}

			this.initAutomaticDiscount = false
		},

		ensurePlatformInfo(retried = false) {
			if (this.$store.state.store.platformInfo) {
				return Promise.resolve(this.$store.state.store.platformInfo)
			}
			return this.$api
				.getPlatformInfo()
				.then(res => {
					const data = res?.data || res || {}
					this.$store.commit('store/SET_PLATFORM_INFO', data)
					return data
				})
				.catch(err => {
					// 帶了無效 session token 導致 401，攔截器已清除 token，
					// 以匿名身份重試公開 API 應可成功
					if (
						!retried &&
						err?.status === 401 &&
						err?.errorCode === 'SESSION_INVALID'
					) {
						$devLog('[App] getPlatformInfo SESSION_INVALID，自動重試')
						return this.ensurePlatformInfo(true)
					}

					console.error('[App] getPlatformInfo 失敗:', err)
					const fallbackData = {}
					this.$store.commit('store/SET_PLATFORM_INFO', fallbackData)

					$devLog('[App] 使用降級模式：platformInfo 設為空物件')
					return fallbackData
				})
		},
		async getStore() {
			const qrCodeHash = this.$storeManager.qrCodeHash
			// 身份以 token 為準（kiosk.is）：本函數只在初始化完成後執行，此時 URL 的
			// ?kiosk= 啟發式已無必要，且站內導航會遺失 query，靠它判斷會誤送 GUEST
			const loginType = this.$store.getters.isKiosk ? 'FRONT' : 'GUEST'

			await this.$api
				.getStore({ qrCodeHash, loginType })
				.then(async res => {
					const storeName = res.data.name
					this.$store.commit('user/SET_STORE', {
						qrCodeHash,
						storeId: res.data.id,
						storeName: storeName,
					})
					document.title = storeName ? storeName : '點溡點餐平台'
					this.pageTitle = storeName
						? storeName + ' | 線上點餐'
						: '點溡點餐平台'

					this.$store.commit('user/CHECK_CART_AND_SET')

					const isDiscountListReady =
						await this.getActiveAutomaticDiscountList()

					if (isDiscountListReady) {
						this.$store.commit('user/CLEAR_CURRENT_ORDER_AUTOMATIC_DISCOUNT')
						if (this.cart.length > 0) {
							this.cartItemSetAutomaticDiscount()
						}
						this.$store.commit('user/SET_CURRENT_ORDER_AUTOMATIC_DISCOUNT', {
							cartTotal: this.cartTotal,
							list: this.orderAutomaticDiscountList,
						})
						if (this.cartHasFreeItem) {
							// freeItem要等到 商品折扣設置完 => 訂單折扣設置 完後才能檢查
							this.checkFreeItem()
						}
					}
					this.initAutomaticDiscount = false
				})
				.catch(err => {
					if (err === '找不到該店家') {
						this.handleGetStoreIdFail(err)
					}
				})
		},
		handleGetStoreIdFail(message) {
			this.$alert(message, 'danger')
			this.$store.commit('user/SET_STORE', {
				qrCodeHash: null,
				storeId: null,
				storeName: '',
			})
			document.title = '點溡點餐平台'
			this.$router.replace('/null/Error')
		},
		async checkBuyer() {
			if (
				this.$store.getters.isKiosk ||
				this.$route.path === `/${this.$route.params.random}/Refresh`
			) {
				await this.handleUserBuyer()
			} else {
				await this.handleGuestBuyer()
			}
		},
		async handleUserBuyer() {
			try {
				const userLogin = await this.$api.user.getCurrentBuyer()
				const { errorCode, data } = userLogin
				if (errorCode === 'SUCCESS') {
					if (data.loginType === 'FRONT') {
						if (this.judgeHasStorePermission(data)) {
							this.$store.commit('kiosk/SET_IS', true)

							if (this.$route.path !== `/${this.$route.params.random}/Store`) {
								await this.$router.replace(
									`/${this.$route.params.random}/Store`,
								)
							}
							return
						}
					}
				}
			} catch (err) {
				// getCurrentBuyer 失敗（例如 401 未登入），繼續執行導向 Refresh 邏輯
				console.warn('[App] getCurrentBuyer 失敗，導向前台登入頁:', err)
			}

			// 清除狀態
			await this.$store.dispatch('user/clearKioskSession')

			await navigateToRefresh(this.$route.params.random)
		},
		judgeHasStorePermission(data) {
			const { permissionList, storeList } = data
			if (
				permissionList.includes('USER_MANAGER') ||
				permissionList.includes('USER_SUPER_MANAGER')
			) {
				return true
			}
			return storeList.some(store => store.id === this.storeId)
		},
		/**
		 * 處理 sessionStorage 不可用時的重試邏輯（使用 URL 參數）
		 * @param {Error} err - 錯誤對象
		 */
		handleGuestLoginRetryWithUrlParams(err) {
			const urlParams = new URLSearchParams(window.location.search)
			let retryCount = parseInt(urlParams.get('_guestLoginRetry') || '0', 10)
			const lastRetryTimestamp = parseInt(
				urlParams.get('_guestLoginRetryTime') || '0',
				10,
			)
			const now = Date.now()

			// 如果距離上次重試超過設定時間，重置計數
			if (now - lastRetryTimestamp > GUEST_LOGIN_RETRY_RESET_TIME) {
				retryCount = 0
			}

			if (retryCount >= GUEST_LOGIN_RETRY_LIMIT) {
				console.error(
					'[App] guestLogin 已達重試上限（sessionStorage 不可用），停止重試',
					err,
				)
				urlParams.delete('_guestLoginRetry')
				urlParams.delete('_guestLoginRetryTime')
				const newUrl = urlParams.toString()
					? `${window.location.pathname}?${urlParams.toString()}`
					: window.location.pathname
				window.history.replaceState({}, '', newUrl)
				this.$alert('無法連線至伺服器，請稍後再試或聯繫客服人員', 'danger')
				return
			}

			console.warn(
				`[App] sessionStorage 不可用，使用 URL 參數重試... (${
					retryCount + 1
				}/${GUEST_LOGIN_RETRY_LIMIT})`,
			)

			// 增加重試次數並 reload
			urlParams.set('_guestLoginRetry', (retryCount + 1).toString())
			urlParams.set('_guestLoginRetryTime', now.toString())
			const newSearch = urlParams.toString()
			window.location.search = newSearch ? '?' + newSearch : ''
		},
		/**
		 * 處理 sessionStorage 可用時的重試邏輯
		 * @param {Error} err - 錯誤對象
		 */
		handleGuestLoginRetryWithSessionStorage(err) {
			// 取得當前重試狀態
			let retryCount = parseInt(
				sessionStorage.getItem(GUEST_LOGIN_RETRY_KEY) || '0',
				10,
			)
			const lastRetryTimestamp = parseInt(
				sessionStorage.getItem(GUEST_LOGIN_RETRY_TIMESTAMP_KEY) || '0',
				10,
			)
			const now = Date.now()

			// 如果距離上次重試超過設定時間，重置計數
			if (now - lastRetryTimestamp > GUEST_LOGIN_RETRY_RESET_TIME) {
				retryCount = 0
			}

			// 檢查是否超過重試限制
			if (retryCount >= GUEST_LOGIN_RETRY_LIMIT) {
				console.error('[App] guestLogin 已達重試上限，停止重試', err)
				sessionStorage.removeItem(GUEST_LOGIN_RETRY_KEY)
				sessionStorage.removeItem(GUEST_LOGIN_RETRY_TIMESTAMP_KEY)
				this.$alert('無法連線至伺服器，請稍後再試或聯繫客服人員', 'danger')
				return
			}

			// 增加重試次數並執行重試
			const newRetryCount = retryCount + 1
			sessionStorage.setItem(GUEST_LOGIN_RETRY_KEY, newRetryCount.toString())
			sessionStorage.setItem(GUEST_LOGIN_RETRY_TIMESTAMP_KEY, now.toString())
			console.warn(
				`[App] guestLogin 失敗，重試中... (${newRetryCount}/${GUEST_LOGIN_RETRY_LIMIT})`,
			)
			location.reload()
		},
		handleGuestBuyer() {
			// 自動登入靠「authToken 是否存在」判斷，但 token 要等回應落地才有，
			// 期間的連續路由事件會各觸發一次（kiosk 權限失敗降級轉訪客時三連發）；
			// single-flight 讓所有觸發點共用同一發進行中的登入
			if (!this.guestLoginPromise) {
				this.guestLoginPromise = this.performGuestLogin().finally(() => {
					this.guestLoginPromise = null
				})
			}
			return this.guestLoginPromise
		},
		async performGuestLogin() {
			try {
				const response = await this.$api.user.guestLogin()
				this.$store.commit('kiosk/SET_IS', false)
				this.$store.commit('user/SET_GUEST_ID', response.data.account)
				this.$store.commit('user/SET_WS_TOKEN', response.wsToken)

				// 成功後清除重試計數
				if (isStorageAvailable()) {
					sessionStorage.removeItem(GUEST_LOGIN_RETRY_KEY)
					sessionStorage.removeItem(GUEST_LOGIN_RETRY_TIMESTAMP_KEY)
				}

				// 清除 URL 參數中的重試記錄
				const urlParams = new URLSearchParams(window.location.search)
				if (
					urlParams.has('_guestLoginRetry') ||
					urlParams.has('_guestLoginRetryTime')
				) {
					urlParams.delete('_guestLoginRetry')
					urlParams.delete('_guestLoginRetryTime')
					const newUrl = urlParams.toString()
						? `${window.location.pathname}?${urlParams.toString()}`
						: window.location.pathname
					window.history.replaceState({}, '', newUrl)
				}
			} catch (err) {
				this.$store.commit('user/CLEAR_AUTH_TOKENS')
				this.$store.commit('kiosk/SET_IS', false)
				if (this.$store.state.maintenance.modal) {
					return
				}

				// 根據 sessionStorage 可用性選擇重試策略
				if (!isStorageAvailable()) {
					this.handleGuestLoginRetryWithUrlParams(err)
				} else {
					this.handleGuestLoginRetryWithSessionStorage(err)
				}
			}
		},
		scheduleReconnectSettingRefresh() {
			if (this.reconnectSettingSyncTimer) return

			const wait = Math.max(
				0,
				RECONNECT_SETTING_SYNC_INTERVAL -
					(Date.now() - this.lastReconnectSettingSyncAt),
			)
			this.reconnectSettingSyncTimer = setTimeout(async () => {
				this.reconnectSettingSyncTimer = null
				if (!this.hasInitialized || !this.storeId) return

				this.lastReconnectSettingSyncAt = Date.now()
				try {
					await this.getOpenInfo()
				} catch (error) {
					$devLog('[App] 重連後更新店家設定失敗', error)
				}
			}, wait)
		},
		getOpenInfo() {
			if (this.storeId == null) return
			return this.$api.getOpenInfo(this.storeId).then(res => {
				this.$store.commit('store/SET_TAKE_OPEN_INFO', res.data)
			})
		},
		initWebSocket() {
			if (
				this.storeId == null ||
				!this.isGuest ||
				this.$store.state.socket.isSocketInitialized
			)
				return
			this.$store.dispatch('socket/initWebSocket')
		},
		onCloseCancelOrderDialog() {
			this.$store.state.isCancelOrderDialog = false
		},
		checkWhatsEatBinding() {
			if (
				!this.$store.state.user.showWhatsEatCoupon ||
				!this.$store.state.store.openInfo?.whatsEatSwitch
			) {
				return Promise.resolve()
			}
			return this.$whatsEatApi
				.checkBinding(this.storeId)
				.then(res => {
					if (res.status === 200) {
						const count = res.data.data.availableTickets?.length || 0
						const shopId = res.data.data.binding.shopId
						this.$store.commit('store/SET_WHATS_EAT_SHOP_ID', shopId)
						this.$store.commit('store/SET_WHATS_EAT_COUPON_AMOUNT', count)
					} else {
						this.$store.commit('store/SET_WHATS_EAT_SHOP_ID', false)
						this.$store.commit('store/SET_WHATS_EAT_COUPON_AMOUNT', null)
					}
				})
				.catch(() => {
					this.$store.commit('store/SET_WHATS_EAT_SHOP_ID', false)
					this.$store.commit('store/SET_WHATS_EAT_COUPON_AMOUNT', null)
				})
		},
		refresh() {
			return window.location.reload()
		},
		onCloseOperationHasFailedDialog() {
			this.$store.commit('user/SET_OPERATION_HAS_FAILED', false)
			this.refresh()
		},
		onGlobalLoadingClose() {
			const gl = this.$store.state.globalLoading
			// counter > 0 代表有新的 loading session 進行中，不應關閉
			if (gl.counter > 0) return
			const onClose = gl.onClose
			this.$store.commit('HIDE_GLOBAL_LOADING')
			if (typeof onClose === 'function') {
				onClose()
			}
		},
		getActiveAutomaticDiscountList() {
			// 可能沿用上一間店的 ready=true；發請求前先重置，失敗時維持未就緒。
			this.$store.commit('store/SET_AUTOMATIC_DISCOUNT_LIST_READY', false)
			const filter = this.isKiosk ? 'forFrontOrder' : 'forMobileOrder'
			return this.$api
				.getActiveAutomaticDiscountList(this.storeId)
				.then(res => {
					if (res.status && res.data.length) {
						const forOrderList = []
						const forItemList = []
						res.data.forEach(automaticDiscount => {
							if (
								automaticDiscount.discountMethod === 'ITEM_SPECIFIC' &&
								automaticDiscount[filter]
							) {
								forItemList.push(automaticDiscount)
							} else if (
								automaticDiscount.discountMethod === 'MINIMUM_SPENDING' &&
								automaticDiscount[filter]
							) {
								forOrderList.push(automaticDiscount)
							}
						})

						this.$store.commit(
							'store/SET_ITEM_AUTOMATIC_DISCOUNT_LIST',
							forItemList,
						)

						this.$store.commit(
							'store/SET_ORDER_AUTOMATIC_DISCOUNT_LIST',
							forOrderList,
						)
					} else if (res.data.length === 0) {
						this.$store.commit('store/SET_ITEM_AUTOMATIC_DISCOUNT_LIST', [])
						this.$store.commit('store/SET_ORDER_AUTOMATIC_DISCOUNT_LIST', [])
					}

					this.$store.commit('store/SET_AUTOMATIC_DISCOUNT_LIST_READY', true)
					return true
				})
				.catch(err => {
					this.$store.commit('store/SET_ITEM_AUTOMATIC_DISCOUNT_LIST', [])
					this.$store.commit('store/SET_ORDER_AUTOMATIC_DISCOUNT_LIST', [])
					this.$alert('取得自動折扣錯誤 : ' + err, 'danger')
					return false
				})
		},
		cartItemSetAutomaticDiscount() {
			this.cart.forEach(item => {
				this.addAutomaticDiscountToItem(item)
			})
		},
		addAutomaticDiscountToItem(item) {
			// 公式與「接受新價」共用 utils/order.js 的純函數，避免兩處各自計算而漂移
			const patch = resolveItemAutomaticDiscount(
				item,
				this.itemAutomaticDiscountList,
			)
			// 贈品：折抵由訂單自動折扣處理，維持原本不動欄位的行為
			if (!patch) {
				return
			}
			Object.assign(item, patch)
		},
		checkFreeItem() {
			const cartFreeItem = this.cart.find(item => item.freeItem)
			if (!cartFreeItem) {
				return
			}
			if (
				cartFreeItem.itemId !==
				this.useOrderAutomaticDiscount?.automaticDiscountItemList[0].itemId
			) {
				cartFreeItem.freeItem = false
				cartFreeItem.automaticDiscountId = null
				cartFreeItem.automaticDiscountName = null
				cartFreeItem.automaticDiscountItemId = null
				cartFreeItem.discountPrice = null
			}
		},
	},
}
</script>

<style lang="sass" scoped>
.refresh-count-down
	font-size: 22px
	color: #e83939
</style>
